import {Ref, useEffect, useState} from 'react';
import useBlockContextMenuHook from '../../../../common/useBlockContextMenu.hook';
import useTypedDispatch from '../../../../common/useTypedDispatch';
import {Loading} from '../../../../design-system/atoms/Loading/Loading';
import {SlideType} from '../../../../graphql/generated/types';
import {ImageViewerContainer} from '../viewer/[productId]/container/ImageViewerContainer';
import {TextViewerContainer} from '../viewer/[productId]/container/TextViewerContainer';
import {PreviewImageViewerFetcherHeadless} from '../viewer/[productId]/headless/PreviewImageViewerFetcherHeadless';
import {PreviewTextViewerFetcherHeadless} from '../viewer/[productId]/headless/PreviewTextViewerFetcherHeadless';
import {ViewerSettingLocalStorageFetcherHeadless} from '../viewer/[productId]/headless/ViewerSettingLocalStorageFetcherHeadless';
import {useFetchViewerInfo} from '../viewer/[productId]/hook/useFetchViewerInfo';
import {useNormalTextViewerCoreOption} from '../viewer/[productId]/hook/useNormalTextViewerCoreOption.hook';
import useWatermark from '../viewer/[productId]/hook/useWatermark';
import {setCurrentPage} from '../viewer/[productId]/state/imageViewerSlice';
import {clearCurrentProgress} from '../viewer/[productId]/state/textViewerSlice';

export function ContentPreviewViewerContainer({
  seriesId,
  productId,
  viewerWrapperRef,
  dimmedAreaRef,
  textViewerRef,
}: {
  seriesId: number;
  productId: number;
  viewerWrapperRef: Ref<HTMLDivElement>;
  dimmedAreaRef: Ref<HTMLDivElement>;
  textViewerRef: Ref<HTMLDivElement>;
}) {
  useBlockContextMenuHook();
  const {isLoading, data} = useFetchViewerInfo({
    seriesId,
    productId,
  });
  const [progressCleaned, setProgressCleaned] = useState(false);
  const dispatch = useTypedDispatch();

  useWatermark();

  useEffect(() => {
    if (data?.item && !progressCleaned) {
      // 최초 진입 시점에 이전에 저장되어 있던 progress를 모두 제거합니다.
      if (data.item.slideType === SlideType.Comic) {
        dispatch(setCurrentPage({productId, page: 1}));
      } else {
        dispatch(clearCurrentProgress({productId}));
      }
      setProgressCleaned(true);
    }
  }, [data?.item, dispatch, productId, progressCleaned]);

  const {backgroundColor} = useNormalTextViewerCoreOption();

  if (!isLoading && !data) {
    return null;
  }

  return (
    <>
      {isLoading && (
        <div className="mb-44pxr flex-center-full">
          <Loading />
        </div>
      )}
      {!isLoading && (
        <div className="mb-44pxr w-full px-11pxr" ref={viewerWrapperRef}>
          <ViewerSettingLocalStorageFetcherHeadless
            seriesId={seriesId}
            productId={productId}
            isPreview={true}
          >
            {data?.item.slideType === SlideType.Comic && (
              <PreviewImageViewerFetcherHeadless
                seriesId={seriesId}
                productId={productId}
              >
                <div className="relative flex w-full grow flex-col overflow-hidden rounded-t-12pxr">
                  <ImageViewerContainer
                    seriesId={seriesId}
                    productId={productId}
                    isPreview={true}
                  />
                  <div
                    ref={dimmedAreaRef}
                    className="absolute top-0 left-0 h-full w-full bg-theme-transparent-10"
                  />
                </div>
              </PreviewImageViewerFetcherHeadless>
            )}
            {data?.item.slideType === SlideType.Epub && (
              <PreviewTextViewerFetcherHeadless
                seriesId={seriesId}
                productId={productId}
              >
                <div
                  className="relative flex w-full grow flex-col overflow-hidden rounded-t-12pxr"
                  style={{
                    backgroundColor,
                  }}
                >
                  <TextViewerContainer
                    seriesId={seriesId}
                    productId={productId}
                    isCmsPreview={false}
                    isPreview={true}
                    viewerRef={textViewerRef}
                  />
                  <div
                    ref={dimmedAreaRef}
                    className="absolute top-0 left-0 h-full w-full bg-theme-transparent-10"
                  />
                </div>
              </PreviewTextViewerFetcherHeadless>
            )}
          </ViewerSettingLocalStorageFetcherHeadless>
        </div>
      )}
    </>
  );
}
